import React from 'react';
import { motion } from 'framer-motion';
import './Hero.css';

const APP_STORE_URL = 'https://apps.apple.com/us/app/trackable-io/id6755646710';

const chatMessages = [
  { type: 'user', text: "Why am I not losing weight this week?" },
  { type: 'ai', text: "Looking at your data: you've been consistent with workouts (4 sessions), but your average daily calories increased by 340. Your sleep also dropped to 5.8hrs average. Let's focus on getting back to 7+ hours - poor sleep increases cortisol and hunger hormones." }
];

function Hero() {
  return (
    <section className="hero">
      <video
        className="hero-bg-video hero-bg-left"
        autoPlay
        muted
        loop
        playsInline
        src="/videos/Hugo.MOV"
      />
      <video
        className="hero-bg-video hero-bg-right"
        autoPlay
        muted
        loop
        playsInline
        src="/videos/Sayo.mov"
      />
      <div className="hero-bg-overlay" />

      <div className="container hero-container">
        <motion.div 
          className="hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <div className="hero-badge">
            <span className="badge-dot" />
            AI-Powered Fitness Coaching
          </div>
          
          <h1 className="hero-title">
            Your Personal AI Coach
            <span className="gradient-text"> That Knows You</span>
          </h1>
          
          <p className="hero-subtitle">
            24/7 personalized guidance based on your workouts, nutrition, sleep, and progress. 
            Not generic advice - real insights from your actual data.
          </p>
          
          <div className="hero-cta">
            <motion.a 
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <svg className="apple-icon" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              Download on App Store
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </motion.a>
            <span className="cta-note">Free on iOS</span>
          </div>
        </motion.div>

        <motion.div 
          className="hero-visual"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
        >
          <div className="chat-mockup">
            <div className="chat-header">
              <div className="ai-avatar">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="url(#ai-grad)" strokeWidth="1.5"/>
                  <path d="M12 6v2M12 16v2M6 12h2M16 12h2" stroke="url(#ai-grad)" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="12" cy="12" r="3" fill="url(#ai-grad)"/>
                  <defs>
                    <linearGradient id="ai-grad" x1="4" y1="4" x2="20" y2="20">
                      <stop stopColor="#667eea"/>
                      <stop offset="1" stopColor="#764ba2"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div className="chat-header-text">
                <span className="chat-title">AI Coach</span>
                <span className="chat-status">Analyzing your data...</span>
              </div>
            </div>
            
            <div className="chat-messages">
              {chatMessages.map((msg, i) => (
                <motion.div 
                  key={i}
                  className={`chat-message ${msg.type}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 + i * 0.3 }}
                >
                  {msg.text}
                </motion.div>
              ))}
              <motion.div 
                className="typing-indicator"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4 }}
              >
                <span /><span /><span />
              </motion.div>
            </div>
          </div>
          
          <div className="floating-cards">
            <motion.div 
              className="stat-card"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div className="stat-icon workout" />
              <div className="stat-info">
                <span className="stat-value">4 workouts</span>
                <span className="stat-label">This week</span>
              </div>
            </motion.div>
            
            <motion.div 
              className="stat-card right"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            >
              <div className="stat-icon calories" />
              <div className="stat-info">
                <span className="stat-value">1,847 cal</span>
                <span className="stat-label">Today</span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
      
      <div className="hero-glow" />
    </section>
  );
}

export default Hero;
